import { useEffect, useRef, useState } from 'react';
import { Switch } from '@/components/ui/switch';
import {
  Move, Trash2, Type, Heading2, Minus, Table2, Landmark, PenLine, FilePlus2,
  Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Undo2, Redo2,
} from 'lucide-react';
import { LETTERHEAD_BG, OPEN_SANS_REGULAR, OPEN_SANS_BOLD, OPEN_SANS_SEMIBOLD, OPEN_SANS_ITALIC, OPEN_SANS_BOLDITALIC } from '@/lib/letterheadAssets';

// ---------------------------------------------------------------------------
// Free-form page canvas: A4 sheets drawn with the fixed letterhead artwork;
// every element is a BOX the user drags anywhere (any page) and resizes by its
// edge handle. Positions are stored in mm ({page, x, y, w}) and the PDF draws
// them at exactly those coordinates. Text boxes are rich-text contentEditable.
// ---------------------------------------------------------------------------

const SCALE = 680 / 210;                    // px per mm
const PAGE_W = 680, PAGE_H = Math.round(297 * SCALE);
const MM_PAGE_H = 297;
const ptPx = (n) => (n * 0.3528 * SCALE);
const pt = (n) => `${ptPx(n).toFixed(1)}px`;

const RICH_SIZES = [8, 9, 10, 11, 12, 14, 18, 22, 26];

// Open Sans + rich-text CSS for the canvas, from the same TTFs embedded in the PDF.
(function injectStyles() {
  if (typeof document === 'undefined' || document.getElementById('opensans-doc-font')) return;
  const s = document.createElement('style');
  s.id = 'opensans-doc-font';
  const faces = [
    ['400', 'normal', OPEN_SANS_REGULAR], ['600', 'normal', OPEN_SANS_SEMIBOLD], ['700', 'normal', OPEN_SANS_BOLD],
    ['400', 'italic', OPEN_SANS_ITALIC], ['700', 'italic', OPEN_SANS_BOLDITALIC],
  ].map(([w, st, b]) => `@font-face{font-family:'OpenSansDoc';src:url(data:font/ttf;base64,${b}) format('truetype');font-weight:${w};font-style:${st};}`).join('\n');
  const sizes = RICH_SIZES.map(n => `.rich-block .fs-${n}{font-size:${pt(n)};}`).join('\n');
  s.textContent = `${faces}
.rich-block{outline:none;}
.rich-block p,.rich-block div{margin:0;min-height:1em;}
.rich-block ul{list-style:disc;margin:0;padding-left:${(6 * SCALE).toFixed(0)}px;}
.rich-block ol{list-style:decimal;margin:0;padding-left:${(6 * SCALE).toFixed(0)}px;}
.rich-block li{margin:0 0 ${pt(1.5)} 0;}
.rich-block[data-empty="true"]:before{content:attr(data-placeholder);color:#9CA3AF;position:absolute;pointer-events:none;}
${sizes}`;
  document.head.appendChild(s);
})();

let idSeq = Date.now();
const nid = () => ++idSeq;
const esc = (t) => String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const inr = (n) => (Number(n) || 0).toLocaleString('en-IN');

const FONT = { fontFamily: "'OpenSansDoc', 'Open Sans', sans-serif" };
const BODY = { ...FONT, fontSize: pt(10), lineHeight: 1.45, color: '#28282C' };

const box = (page, x, y, w, type, extra = {}) => ({ id: nid(), page, x, y, w, type, ...extra });

// HTML for the per-lead header boxes. Tagged with `role` so a saved template
// can be re-personalised for a new lead without touching its layout.
const headerHtml = {
  date: ({ date }) => `<p style="text-align: right">${esc(date || '')}</p>`,
  title: ({ title }) => `<p><b><span class="fs-22">${esc((title || 'Design Proposal').toUpperCase())}</span></b></p>`,
  to: ({ client_name, re }) => `<p><b>To,</b></p><p><b>${esc(client_name || '')}</b></p>${re ? `<p><b>Re: ${esc(re)}</b></p>` : ''}`,
};

// Re-seed a saved template layout for a specific lead: keeps every box exactly
// where the template put it, but rewrites the date/title/To lines.
export function applyTemplateBoxes(templateBoxes, ctx = {}) {
  return (templateBoxes || []).map((b) => {
    const fresh = { ...b, id: nid() };
    if (b.role && headerHtml[b.role]) fresh.html = headerHtml[b.role](ctx);
    return fresh;
  });
}

// Default document: text boxes laid out like the original letterhead sample.
export function buildDefaultBoxes({ title, date, client_name, re, quotation_note, scope_heading, scope_items, terms, closing_note } = {}) {
  const boxes = [
    box(0, 140, 14, 57, 'text', { role: 'date', html: headerHtml.date({ date }) }),
    box(0, 73, 20, 116, 'text', { role: 'title', html: headerHtml.title({ title }) }),
    box(0, 73, 34, 124, 'text', { role: 'to', html: headerHtml.to({ client_name, re }) }),
    box(0, 73, 58, 124, 'table'),
    box(0, 73, 100, 124, 'text', { html: quotation_note ? `<p>${esc(quotation_note)}</p>` : '' }),
    box(0, 73, 116, 124, 'heading', { text: scope_heading || 'Scope of Work - Design Consultation' }),
    box(0, 73, 126, 124, 'text', { html: `<ul>${(scope_items || []).map(i => `<li>${esc(i)}</li>`).join('')}</ul>` }),
    box(1, 73, 24, 124, 'heading', { text: 'Scope of Work / Terms & Conditions' }),
    box(1, 73, 34, 124, 'text', { html: `<p>Terms &amp; conditions are as follows:</p><ul>${(terms || []).map(t => `<li>${esc(t)}</li>`).join('')}</ul>` }),
    box(1, 73, 168, 124, 'bank'),
    box(1, 73, 210, 124, 'text', { html: closing_note ? `<p>${esc(closing_note)}</p>` : '' }),
    box(1, 73, 228, 124, 'signoff'),
  ];
  return { boxes, pageCount: 2 };
}

// Default invoice: same letterhead, INVOICE title, no scope/terms, bank always shown.
export function buildDefaultInvoiceBoxes({ date, client_name, invoice_number, closing_note } = {}) {
  const boxes = [
    box(0, 140, 14, 57, 'text', { html: `<p style="text-align: right">${esc(date || '')}</p>` }),
    box(0, 73, 20, 116, 'text', { html: `<p><b><span class="fs-22">INVOICE</span></b></p>` }),
    box(0, 73, 34, 124, 'text', { html: `<p><b>To,</b></p><p><b>${esc(client_name || '')}</b></p>${invoice_number ? `<p><b>Invoice No: ${esc(invoice_number)}</b></p>` : ''}` }),
    box(0, 73, 58, 124, 'table'),
    box(0, 73, 112, 124, 'bank'),
    box(0, 73, 155, 124, 'text', { html: `<p>${esc(closing_note || 'Thank you for your business.')}</p>` }),
    box(0, 73, 170, 124, 'signoff'),
  ];
  return { boxes, pageCount: 1 };
}

// Convert an older flow-block list (stacked layout) into positioned boxes.
export function convertToBoxes(blocks) {
  const estH = (b) => {
    if (b.type === 'heading') return 12;
    if (b.type === 'line') return 4;
    if (b.type === 'spacer') return Number(b.h) || 6;
    if (b.type === 'table') return 42;
    if (b.type === 'bank') return 34;
    if (b.type === 'signoff') return 14;
    const html = b.html || b.text || '';
    const text = String(html).replace(/<[^>]+>/g, ' ');
    const lis = (String(html).match(/<li/g) || []).length;
    const lines = Math.max(1, Math.ceil(text.length / 85)) + lis;
    return lines * 5.2 + 3;
  };
  const boxes = [];
  let page = 0, y = 58; // below the old fixed header area
  for (const b of blocks || []) {
    if (b.type === 'pagebreak') { page += 1; y = 24; continue; }
    const h = estH(b);
    if (y + h > 266) { page += 1; y = 24; }
    if (b.type === 'para') boxes.push(box(page, 73, y, 124, 'text', { html: `<p>${esc(b.text)}</p>` }));
    else if (b.type === 'bullets') boxes.push(box(page, 73, y, 124, 'text', { html: `<ul>${(b.items || []).filter(Boolean).map(i => `<li>${esc(i)}</li>`).join('')}</ul>` }));
    else if (b.type === 'rich') boxes.push(box(page, 73, y, 124, 'text', { html: b.html || '' }));
    else boxes.push(box(page, 73, y, 124, b.type, { text: b.text, color: b.color }));
    y += h + 4;
  }
  return { boxes, pageCount: page + 1 };
}

// --- rich text editing ------------------------------------------------------

function dispatchInputOnSelection() {
  const sel = window.getSelection();
  const node = sel?.anchorNode;
  const host = (node?.nodeType === 3 ? node.parentElement : node)?.closest?.('.rich-block');
  if (host) host.dispatchEvent(new Event('input', { bubbles: true }));
}

function applyFontSize(sizePt) {
  document.execCommand('fontSize', false, '7');
  document.querySelectorAll('.rich-block font[size="7"]').forEach(f => {
    const span = document.createElement('span');
    span.className = `fs-${sizePt}`;
    span.innerHTML = f.innerHTML;
    f.replaceWith(span);
  });
  dispatchInputOnSelection();
}

function RichToolbar() {
  const btn = "p-1.5 rounded hover:bg-[#D4D0C8] text-[#2C2C2E]";
  const cmd = (c, v) => { document.execCommand(c, false, v); dispatchInputOnSelection(); };
  const stop = (e) => e.preventDefault(); // keep the text selection
  return (
    <div className="flex items-center gap-0.5 px-2 py-1 rounded-lg bg-[#E8E6E1] shadow-sm border border-[#D4D0C8] w-fit" data-testid="rich-toolbar" onMouseDown={stop}>
      <button className={btn} title="Bold" onClick={() => cmd('bold')}><Bold size={13} /></button>
      <button className={btn} title="Italic" onClick={() => cmd('italic')}><Italic size={13} /></button>
      <button className={btn} title="Underline" onClick={() => cmd('underline')}><Underline size={13} /></button>
      <div className="w-px h-4 bg-[#D4D0C8] mx-1" />
      <select title="Font size" defaultValue="" onMouseDown={e => e.stopPropagation()}
        onChange={e => { if (e.target.value) { applyFontSize(Number(e.target.value)); e.target.value = ''; } }}
        className="h-6 text-[11px] rounded border border-[#D4D0C8] bg-white px-1 outline-none">
        <option value="" disabled>Size</option>
        {RICH_SIZES.map(n => <option key={n} value={n}>{n} pt</option>)}
      </select>
      <div className="w-px h-4 bg-[#D4D0C8] mx-1" />
      <button className={btn} title="Bullet list" onClick={() => cmd('insertUnorderedList')}><List size={13} /></button>
      <button className={btn} title="Numbered list" onClick={() => cmd('insertOrderedList')}><ListOrdered size={13} /></button>
      <div className="w-px h-4 bg-[#D4D0C8] mx-1" />
      <button className={btn} title="Align left" onClick={() => cmd('justifyLeft')}><AlignLeft size={13} /></button>
      <button className={btn} title="Align centre" onClick={() => cmd('justifyCenter')}><AlignCenter size={13} /></button>
      <button className={btn} title="Align right" onClick={() => cmd('justifyRight')}><AlignRight size={13} /></button>
    </div>
  );
}

function RichBlock({ html, onChange }) {
  const lastHtml = useRef(null);
  return (
    <div
      ref={el => {
        if (!el) return;
        const focused = el === document.activeElement || el.contains(document.activeElement);
        // Sync external changes (undo/redo) into the DOM, but never while the
        // user is typing in this block — that would jump the caret.
        if (lastHtml.current === null || (!focused && (html || '') !== lastHtml.current)) {
          el.innerHTML = html || '';
          lastHtml.current = html || '';
          el.dataset.empty = el.textContent.trim() ? 'false' : 'true';
        }
      }}
      contentEditable
      suppressContentEditableWarning
      className="rich-block relative min-h-[1.5em] w-full"
      style={BODY}
      data-placeholder="Type text…"
      data-empty={!html}
      onInput={(e) => { lastHtml.current = e.currentTarget.innerHTML; e.currentTarget.dataset.empty = e.currentTarget.textContent.trim() ? 'false' : 'true'; onChange(lastHtml.current); }}
    />
  );
}

// --- palette ----------------------------------------------------------------

const PALETTE = [
  { type: 'text', label: 'Text box', icon: Type },
  { type: 'heading', label: 'Heading', icon: Heading2 },
  { type: 'line', label: 'Line', icon: Minus },
  { type: 'table', label: 'Fee table', icon: Table2 },
  { type: 'bank', label: 'Bank', icon: Landmark },
  { type: 'signoff', label: 'Sign-off', icon: PenLine },
];

// ---------------------------------------------------------------------------

export default function ProposalCanvas({
  boxes, setBoxes, pageCount, setPageCount, proposal, setProposal, showBank, bank,
  subtotal, gstAmount, grandTotal, updateFeeRow, addFeeRow, removeFeeRow,
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [, setHistVer] = useState(0); // re-render for button disabled states
  const pageRefs = useRef({});
  const dragRef = useRef(null);
  const history = useRef([]);
  const future = useRef([]);
  const lastPush = useRef(0);

  // --- undo / redo ----------------------------------------------------------
  const takeState = () => ({
    boxes: JSON.parse(JSON.stringify(boxes)),
    pageCount,
    fee_table: JSON.parse(JSON.stringify(proposal.fee_table || [])),
    gst_included: !!proposal.gst_included,
    sign_name: proposal.sign_name || '',
  });
  // force=true records unconditionally (structural edits); otherwise bursts of
  // changes within 800ms (typing) collapse into one history entry.
  const snapshot = (force = false) => {
    const now = Date.now();
    if (!force && now - lastPush.current < 800) return;
    lastPush.current = now;
    history.current.push(takeState());
    if (history.current.length > 60) history.current.shift();
    future.current = [];
    setHistVer(v => v + 1);
  };
  const applyState = (s) => {
    setBoxes(() => s.boxes);
    setPageCount(() => s.pageCount);
    setProposal(p => ({ ...p, fee_table: s.fee_table, gst_included: s.gst_included, sign_name: s.sign_name }));
  };
  const undo = () => {
    if (!history.current.length) return;
    future.current.push(takeState());
    applyState(history.current.pop());
    lastPush.current = 0;
    setSelectedId(null);
    setHistVer(v => v + 1);
  };
  const redo = () => {
    if (!future.current.length) return;
    history.current.push(takeState());
    applyState(future.current.pop());
    lastPush.current = 0;
    setSelectedId(null);
    setHistVer(v => v + 1);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const t = e.target;
      // inside a text field the browser's own undo handles typing
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const k = e.key.toLowerCase();
      if (k === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
      else if (k === 'y') { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // --- mutations (all record history) ---------------------------------------
  const patchRaw = (id, updates) => setBoxes(prev => prev.map(b => (b.id === id ? { ...b, ...updates } : b)));
  const patch = (id, updates) => { snapshot(); patchRaw(id, updates); };
  const remove = (id) => { snapshot(true); setBoxes(prev => prev.filter(b => b.id !== id)); setSelectedId(null); };

  const addBox = (pageIdx, type) => {
    snapshot(true);
    const onPage = boxes.filter(b => b.page === pageIdx);
    const y = clamp(onPage.length ? Math.max(...onPage.map(b => b.y)) + 14 : 30, 14, 250);
    const extra = type === 'text' ? { html: '' } : type === 'heading' ? { text: 'New Section' } : type === 'line' ? { color: 'gold' } : {};
    const nb = box(pageIdx, 73, y, type === 'line' ? 124 : 124, type, extra);
    setBoxes(prev => [...prev, nb]);
    setSelectedId(nb.id);
  };

  const addPage = () => { snapshot(true); setPageCount(pc => pc + 1); };
  const removePage = (pi) => {
    snapshot(true);
    setBoxes(prev => prev.filter(b => b.page !== pi).map(b => (b.page > pi ? { ...b, page: b.page - 1 } : b)));
    setPageCount(pc => Math.max(1, pc - 1));
  };

  // fee-table edits flow through the parent; record them here so undo covers them
  const hUpdateFeeRow = (idx, key, val) => { snapshot(); updateFeeRow(idx, key, val); };
  const hAddFeeRow = () => { snapshot(true); addFeeRow(); };
  const hRemoveFeeRow = (idx) => { snapshot(true); removeFeeRow(idx); };

  // --- pointer drag / resize ------------------------------------------------
  const pageAtPointer = (clientX, clientY) => {
    let best = null, bestDist = Infinity;
    Object.entries(pageRefs.current).forEach(([k, el]) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (clientY >= r.top && clientY <= r.bottom) { best = { pi: Number(k), rect: r }; bestDist = 0; }
      else {
        const d = clientY < r.top ? r.top - clientY : clientY - r.bottom;
        if (d < bestDist) { bestDist = d; best = { pi: Number(k), rect: r }; }
      }
    });
    return best;
  };

  const startMove = (e, b) => {
    e.preventDefault(); e.stopPropagation();
    setSelectedId(b.id);
    const pr = pageRefs.current[b.page]?.getBoundingClientRect();
    if (!pr) return;
    snapshot(true); // one history entry per drag
    dragRef.current = {
      id: b.id, mode: 'move',
      grabX: (e.clientX - pr.left) / SCALE - b.x,
      grabY: (e.clientY - pr.top) / SCALE - b.y,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* synthetic events */ }
  };

  const startResize = (e, b) => {
    e.preventDefault(); e.stopPropagation();
    setSelectedId(b.id);
    snapshot(true); // one history entry per resize
    dragRef.current = { id: b.id, mode: 'resize', startClientX: e.clientX, startW: b.w, x: b.x };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* synthetic events */ }
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    if (d.mode === 'move') {
      const hit = pageAtPointer(e.clientX, e.clientY);
      if (!hit) return;
      const b = boxes.find(x => x.id === d.id);
      if (!b) return;
      const nx = clamp(Math.round(((e.clientX - hit.rect.left) / SCALE - d.grabX) * 2) / 2, 0, 210 - b.w);
      const ny = clamp(Math.round(((e.clientY - hit.rect.top) / SCALE - d.grabY) * 2) / 2, 0, MM_PAGE_H - 6);
      patchRaw(d.id, { x: nx, y: ny, page: hit.pi });
    } else {
      const dw = (e.clientX - d.startClientX) / SCALE;
      patchRaw(d.id, { w: clamp(Math.round((d.startW + dw) * 2) / 2, 25, 210 - d.x) });
    }
  };

  const endDrag = () => { dragRef.current = null; };

  // --- box content ----------------------------------------------------------

  const renderContent = (b, sel) => {
    switch (b.type) {
      case 'text':
        return <RichBlock html={b.html} onChange={htmlVal => patch(b.id, { html: htmlVal })} />;
      case 'heading':
        return (
          <div>
            <input value={b.text || ''} onChange={e => patch(b.id, { text: e.target.value })}
              className="w-full bg-transparent outline-none uppercase"
              style={{ ...FONT, fontSize: pt(12), fontWeight: 700, color: '#F2A61D', letterSpacing: '0.02em' }} />
            <div style={{ borderBottom: '2.3px solid #F2A61D', marginTop: 2 }} />
          </div>
        );
      case 'line': {
        const colors = { gold: '#F2A61D', grey: '#C8C8C8', dark: '#28282C', teal: '#3A8C8C' };
        const hex = colors[b.color] || colors.gold;
        const weight = Number(b.weight) || 0.7; // mm
        return (
          <div className="py-1">
            <div style={{ borderBottom: `${Math.max(1, weight * SCALE).toFixed(1)}px solid ${hex}` }} />
            {sel && (
              <div className="absolute left-0 top-full mt-1 z-20 flex items-center gap-1.5 px-2 py-1 rounded-md bg-white border border-[#D4D0C8] shadow-sm"
                onPointerDown={e => e.stopPropagation()} data-testid={`line-controls-${b.id}`}>
                {Object.entries(colors).map(([name, c]) => (
                  <button key={name} title={name} onClick={() => patch(b.id, { color: name })}
                    className={`w-4 h-4 rounded-full border ${b.color === name || (!b.color && name === 'gold') ? 'ring-2 ring-offset-1 ring-[#3A8C8C]' : 'border-[#D4D0C8]'}`}
                    style={{ background: c }} />
                ))}
                <div className="w-px h-4 bg-[#D4D0C8] mx-0.5" />
                <span className="text-[10px] text-[#6B7280]">Weight</span>
                <select value={weight} onChange={e => patch(b.id, { weight: Number(e.target.value) })}
                  className="h-5 text-[10px] rounded border border-[#D4D0C8] bg-white outline-none">
                  {[0.3, 0.5, 0.7, 1, 1.5, 2, 3].map(wt => <option key={wt} value={wt}>{wt} mm</option>)}
                </select>
              </div>
            )}
          </div>
        );
      }
      case 'table': {
        const labels = { desc: 'DESCRIPTION', qty: 'QTY.', rate: 'RATE', price: 'PRICE', ...(b.labels || {}) };
        const showQty = b.show_qty !== false;
        const showRate = b.show_rate !== false;
        const cw = { qty: 20, rate: 20, price: 26, ...(b.col_widths || {}) }; // mm
        const rowPad = b.row_pad != null ? Number(b.row_pad) : 1.2;           // mm each side
        const padStyle = { paddingTop: rowPad * SCALE, paddingBottom: rowPad * SCALE };
        const fill = (showQty ? 1 : 0) + (showRate ? 1 : 0); // filler cells before the totals label
        const goldB = { borderBottom: '2.3px solid #F2A61D' };
        const headStyle = { ...FONT, color: '#F2A61D', fontWeight: 700, fontSize: pt(11) };
        const headInput = (key, right) => (
          <input value={labels[key]} onChange={e => patch(b.id, { labels: { ...labels, [key]: e.target.value } })}
            className={`w-full bg-transparent outline-none ${right ? 'text-right' : ''}`} style={headStyle} />
        );
        const widthInput = (key) => (
          <input type="number" min={8} max={80} value={cw[key]}
            onChange={e => patch(b.id, { col_widths: { ...cw, [key]: Math.min(80, Math.max(8, Number(e.target.value) || cw[key])) } })}
            className="w-11 h-5 text-[10px] rounded border border-[#D4D0C8] bg-white outline-none px-1" />
        );
        const divTop = { borderTop: '1px solid #DCDCDC' };
        const totalsDivider = proposal.gst_included ? 'subtotal' : 'total';
        return (
          <div>
            {sel && (
              <div className="absolute -top-8 right-0 z-20 flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1 rounded-md bg-white border border-[#D4D0C8] shadow-sm text-[10px] text-[#6B7280] select-none"
                onPointerDown={e => e.stopPropagation()} data-testid={`table-controls-${b.id}`}>
                <label className="flex items-center gap-1"><input type="checkbox" checked={showQty} onChange={e => patch(b.id, { show_qty: e.target.checked })} /> Qty</label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={showRate} onChange={e => patch(b.id, { show_rate: e.target.checked })} /> Rate</label>
                <span className="w-px h-4 bg-[#D4D0C8]" />
                <span>Width (mm):</span>
                {showQty && <label className="flex items-center gap-0.5">Qty {widthInput('qty')}</label>}
                {showRate && <label className="flex items-center gap-0.5">Rate {widthInput('rate')}</label>}
                <label className="flex items-center gap-0.5">Price {widthInput('price')}</label>
                <span className="w-px h-4 bg-[#D4D0C8]" />
                <label className="flex items-center gap-0.5">Row pad
                  <input type="number" min={0} max={6} step={0.5} value={rowPad}
                    onChange={e => patch(b.id, { row_pad: Math.min(6, Math.max(0, Number(e.target.value) || 0)) })}
                    className="w-11 h-5 text-[10px] rounded border border-[#D4D0C8] bg-white outline-none px-1" />
                </label>
              </div>
            )}
            <table className="w-full" style={{ ...FONT, fontSize: pt(10), color: '#28282C', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th className="text-left pb-1" style={goldB}>{headInput('desc')}</th>
                  {showQty && <th className="text-left pb-1" style={{ ...goldB, width: cw.qty * SCALE }}>{headInput('qty')}</th>}
                  {showRate && <th className="text-left pb-1" style={{ ...goldB, width: cw.rate * SCALE }}>{headInput('rate')}</th>}
                  <th className="text-right pb-1" style={{ ...goldB, width: cw.price * SCALE }}>{headInput('price', true)}</th>
                  <th style={{ ...goldB, width: 18 }} />
                </tr>
              </thead>
              <tbody>
                {(proposal.fee_table || []).map((row, idx) => (
                  <tr key={row.id} className="group/fee align-top">
                    <td className="pr-2" style={padStyle}>
                      <textarea value={row.description || ''} rows={1} onChange={e => hUpdateFeeRow(idx, 'description', e.target.value)}
                        ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                        className="w-full resize-none overflow-hidden bg-transparent outline-none" style={BODY} />
                    </td>
                    {showQty && <td className="pr-2" style={padStyle}><input value={row.qty ?? ''} onChange={e => hUpdateFeeRow(idx, 'qty', e.target.value)} className="w-full bg-transparent outline-none" style={BODY} /></td>}
                    {showRate && <td className="pr-2" style={padStyle}><input value={row.rate ?? ''} onChange={e => hUpdateFeeRow(idx, 'rate', e.target.value)} className="w-full bg-transparent outline-none" style={BODY} /></td>}
                    <td className="text-right" style={padStyle}>
                      <span className="inline-flex items-center justify-end gap-0.5 w-full" style={BODY}>
                        Rs <input type="number" value={row.price ?? 0} onChange={e => hUpdateFeeRow(idx, 'price', Number(e.target.value) || 0)}
                          className="w-[70px] bg-transparent outline-none text-right border border-transparent hover:border-[#D4D0C8]/70 focus:border-[#F2A61D] rounded-sm px-0.5"
                          style={{ ...BODY }} data-testid={`fee-price-${idx}`} />
                      </span>
                    </td>
                    <td className="text-right" style={padStyle}>
                      <button onClick={() => hRemoveFeeRow(idx)} className="opacity-0 group-hover/fee:opacity-100 text-[#C4727F]" tabIndex={-1}><Trash2 size={11} /></button>
                    </td>
                  </tr>
                ))}
                <tr><td colSpan={3 + fill}>
                  <button onClick={hAddFeeRow} className="text-[11px] text-[#3A8C8C] hover:underline">+ row</button>
                </td></tr>
                {proposal.gst_included && (
                  <>
                    <tr>
                      {fill > 0 && <td colSpan={fill} style={totalsDivider === 'subtotal' ? divTop : undefined} />}
                      <td className="py-0.5" style={{ ...BODY, ...divTop }}>Subtotal</td>
                      <td className="py-0.5 text-right" style={{ ...BODY, ...divTop }}>Rs {inr(subtotal)}.00</td>
                      <td style={divTop} />
                    </tr>
                    <tr>
                      {fill > 0 && <td colSpan={fill} />}
                      <td className="py-0.5" style={BODY}>GST ({proposal.gst_rate}%)</td>
                      <td className="py-0.5 text-right" style={BODY}>Rs {inr(gstAmount)}.00</td><td />
                    </tr>
                  </>
                )}
                <tr>
                  {fill > 0 && <td colSpan={fill} style={totalsDivider === 'total' ? divTop : undefined} />}
                  <td className="py-0.5" style={{ ...BODY, fontWeight: 700, ...(totalsDivider === 'total' ? divTop : {}) }}>Total</td>
                  <td className="py-0.5 text-right" style={{ ...BODY, fontWeight: 700, ...(totalsDivider === 'total' ? divTop : {}) }}>Rs {inr(grandTotal)}.00</td>
                  <td style={totalsDivider === 'total' ? divTop : undefined} />
                </tr>
              </tbody>
            </table>
            <label className="flex items-center gap-1.5 mt-1 text-[10px] text-[#9CA3AF] select-none">
              <Switch checked={!!proposal.gst_included} onCheckedChange={v => { snapshot(true); setProposal(p => ({ ...p, gst_included: v })); }} className="scale-[0.6]" /> Include GST ({proposal.gst_rate}%)
            </label>
          </div>
        );
      }
      case 'bank': {
        const b2 = showBank ? (bank || {}) : {};
        return (
          <div style={BODY}>
            <div style={{ fontWeight: 600 }}>Account details for payment:</div>
            <div>Account Holder: {b2.account_holder || ''}</div>
            <div>Account Number: {b2.account_number || ''}</div>
            <div>IFSC: {b2.ifsc || ''}</div>
            <div>Branch: {b2.branch || ''}</div>
            <div>Account Type: {b2.account_type || ''}</div>
            <div className="mt-0.5 text-[9px] text-[#9CA3AF] select-none">{showBank ? 'Filled from Settings' : 'Blank until the lead is Won'}</div>
          </div>
        );
      }
      case 'signoff':
        return (
          <div className="text-right" style={BODY}>
            <div>Sincerely,</div>
            <input value={proposal.sign_name || ''} placeholder="Sign-off name"
              onChange={e => { snapshot(); setProposal(p => ({ ...p, sign_name: e.target.value })); }}
              className="bg-transparent outline-none text-right w-full"
              style={{ ...BODY, fontWeight: 600 }} />
          </div>
        );
      default: return null;
    }
  };

  return (
    <div className="overflow-x-auto pb-2" data-testid="proposal-canvas">
      <div className="mx-auto space-y-2" style={{ width: PAGE_W }}>
        <div className="sticky top-2 z-20 flex items-center justify-center gap-2">
          <RichToolbar />
          <div className="flex items-center gap-0.5 px-1.5 py-1 rounded-lg bg-[#E8E6E1] shadow-sm border border-[#D4D0C8]">
            <button onClick={undo} disabled={!history.current.length} title="Undo (Ctrl+Z)" data-testid="canvas-undo"
              className="p-1.5 rounded hover:bg-[#D4D0C8] text-[#2C2C2E] disabled:opacity-30 disabled:hover:bg-transparent"><Undo2 size={13} /></button>
            <button onClick={redo} disabled={!future.current.length} title="Redo (Ctrl+Y)" data-testid="canvas-redo"
              className="p-1.5 rounded hover:bg-[#D4D0C8] text-[#2C2C2E] disabled:opacity-30 disabled:hover:bg-transparent"><Redo2 size={13} /></button>
          </div>
        </div>
        <p className="text-center text-[10px] text-[#9CA3AF] select-none">Click a box to select it — drag the ✥ handle to move it (across pages too), drag the right edge to resize, select text for the toolbar.</p>
        <div className="space-y-6">
          {Array.from({ length: Math.max(1, pageCount) }, (_, pi) => (
            <div key={pi}>
              <div className="flex items-center justify-between mb-1 px-1">
                <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Page {pi + 1} of {Math.max(1, pageCount)}</span>
                {pageCount > 1 && (
                  <button onClick={() => removePage(pi)} className="text-[10px] text-[#C4727F] hover:underline">Remove page</button>
                )}
              </div>
              <div ref={el => { pageRefs.current[pi] = el; }}
                className="relative rounded-md shadow-md border border-[#E8E6E1] overflow-hidden"
                style={{ width: PAGE_W, height: PAGE_H, backgroundImage: `url(${LETTERHEAD_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                onPointerDown={() => setSelectedId(null)}>
                {boxes.filter(b => b.page === pi).map(b => {
                  const sel = selectedId === b.id;
                  return (
                    <div key={b.id}
                      className={`absolute group/box rounded-sm ${sel ? 'ring-2 ring-[#3A8C8C]/70 z-10' : 'hover:ring-1 hover:ring-[#3A8C8C]/30'}`}
                      style={{ left: b.x * SCALE, top: b.y * SCALE, width: b.w * SCALE }}
                      onPointerDown={(e) => { e.stopPropagation(); setSelectedId(b.id); }}>
                      {/* move handle */}
                      <div onPointerDown={(e) => startMove(e, b)} onPointerMove={onPointerMove} onPointerUp={endDrag}
                        className={`absolute -top-2.5 -left-2.5 w-5 h-5 rounded-full bg-[#3A8C8C] text-white flex items-center justify-center cursor-move shadow ${sel ? 'opacity-100' : 'opacity-0 group-hover/box:opacity-90'}`}
                        style={{ touchAction: 'none' }} title="Drag to move" data-testid={`box-move-${b.id}`}>
                        <Move size={11} />
                      </div>
                      {/* delete — sits beside the move handle so it never overlaps the resize grip */}
                      <button onClick={() => remove(b.id)} tabIndex={-1}
                        className={`absolute -top-2.5 left-3.5 w-5 h-5 rounded-full bg-[#C4727F] text-white flex items-center justify-center shadow ${sel ? 'opacity-100' : 'opacity-0 group-hover/box:opacity-90'}`}
                        title="Delete box" data-testid={`box-delete-${b.id}`}>
                        <Trash2 size={10} />
                      </button>
                      {/* resize handle (right edge) */}
                      <div onPointerDown={(e) => startResize(e, b)} onPointerMove={onPointerMove} onPointerUp={endDrag}
                        className={`absolute top-1/2 -translate-y-1/2 -right-1.5 w-3 h-8 rounded-full bg-[#3A8C8C]/80 cursor-ew-resize shadow ${sel ? 'opacity-100' : 'opacity-0 group-hover/box:opacity-70'}`}
                        style={{ touchAction: 'none' }} title="Drag to resize width" data-testid={`box-resize-${b.id}`} />
                      <div className={`${sel ? 'bg-white/40' : ''} rounded-sm`}>{renderContent(b, sel)}</div>
                    </div>
                  );
                })}
              </div>

              {/* Insert palette for this page */}
              <div className="flex flex-wrap items-center gap-1.5 mt-2 px-1" data-testid={`page-palette-${pi}`}>
                {PALETTE.map(({ type, label, icon: Icon }) => (
                  <button key={type} onClick={() => addBox(pi, type)}
                    className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#E8E6E1] text-[#2C2C2E] text-[11px] hover:bg-[#D4D0C8] transition-colors">
                    <Icon size={11} strokeWidth={1.8} /> {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <button onClick={addPage} data-testid="proposal-add-page"
          className="w-full flex items-center justify-center gap-1.5 py-3 mt-4 rounded-md border-2 border-dashed border-[#D4D0C8] text-xs text-[#6B7280] hover:border-[#3A8C8C] hover:text-[#3A8C8C] transition-colors">
          <FilePlus2 size={14} strokeWidth={1.5} /> Add page
        </button>
      </div>
    </div>
  );
}
